import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  structuredData?: any;
  language?: string;
  breadcrumbs?: Array<{name: string, url: string}>;
  noindex?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = '/grass-depot-storefront.png',
  structuredData,
  language = 'en',
  breadcrumbs,
  noindex = false
}) => {
  const fullTitle = `${title} | The Grass Depot LLC - Premium Landscaping Materials Texas`;
  const baseUrl = 'https://thegrassdepot.com';
  const currentUrl = canonical ? `${baseUrl}${canonical}` : `${baseUrl}/${language}`;
  const imageUrl = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;

  // Generate breadcrumb structured data if provided
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${baseUrl}${item.url}`
    }))
  } : null;

  return (
    <Helmet>
      {/* Essential Meta Tags */}
      <html lang={language === 'en' ? 'en-US' : 'es-ES'} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={currentUrl} />
      
      {/* Robots Meta */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={language === 'en' ? 'en_US' : 'es_ES'} />
      <meta property="og:site_name" content="The Grass Depot LLC" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Business & Contact Info */}
      <meta name="author" content="The Grass Depot LLC" />
      <meta name="contact" content="thegrassdepotllc@gmail.com" />
      <meta name="telephone" content="+19364432777" />
      
      {/* Geographic Tags */}
      <meta name="geo.region" content="US-TX" />
      <meta name="geo.placename" content="Montgomery County, Texas" />
      <meta name="geo.position" content="30.1588;-95.4613" />
      <meta name="ICBM" content="30.1588, -95.4613" />
      <meta name="coverage" content="Montgomery County, Texas; Conroe, TX; The Woodlands, TX; Spring, TX; Magnolia, TX" />

      {/* Mobile & Viewport */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="theme-color" content="#5a9918" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Content Language */}
      <meta httpEquiv="content-language" content={language === 'en' ? 'en-US' : 'es-ES'} />
      
      {/* Language Alternates */}
      <link rel="alternate" hrefLang="en" href={`${baseUrl}/en${canonical || ''}`} />
      <link rel="alternate" hrefLang="es" href={`${baseUrl}/es${canonical || ''}`} />
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/en${canonical || ''}`} />

      {/* Structured Data - Organization/Business */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Breadcrumbs Structured Data */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* Additional SEO Tags */}
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Performance Hints */}
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
    </Helmet>
  );
};

export default SEO;